import {
  addLegacyEffect,
  buildLegacyEffectMetadata,
  getLegacyEffectPatch,
  removeLegacyEffectAt,
  removeLegacyEffect,
} from './writeAdapters';
import {
  getLegacyEffectGlobalOccurrence,
  lookupLegacyEffectData,
} from './effectModel';

const toArray = (value) => Array.isArray(value) ? value : [];

export const buildDiseaseState = ({
  diseaseId,
  id = null,
  contractedDate = null,
  overrides = null,
} = {}) => {
  const normalizedDiseaseId = String(diseaseId || '').trim();
  if (!normalizedDiseaseId) return null;

  const entry = {
    diseaseId: normalizedDiseaseId,
    active: true,
    applyEffects: true,
    applyHiddenEffects: false,
    contractedDate: contractedDate || new Date().toISOString(),
    showName: false,
    visibleSymptoms: [],
    showEffects: false,
    showCure: false,
    ...(overrides || {}),
  };

  if (id) entry.id = id;
  return entry;
};

export const addDiseaseState = (char = {}, diseaseEntry = null) => {
  if (!diseaseEntry?.diseaseId) return char;
  if (toArray(char.diseaseList).some((entry) => entry?.diseaseId === diseaseEntry.diseaseId)) {
    return char;
  }
  return {
    ...char,
    diseaseList: [...toArray(char.diseaseList), diseaseEntry],
  };
};

export const removeDiseaseState = (char = {}, diseaseId) => {
  const normalizedDiseaseId = String(diseaseId || '').trim();
  if (!normalizedDiseaseId) return char;
  return {
    ...char,
    diseaseList: toArray(char.diseaseList).filter((entry) => entry?.diseaseId !== normalizedDiseaseId),
  };
};

export const updateDiseaseState = (char = {}, diseaseId, updates = {}) => {
  const normalizedDiseaseId = String(diseaseId || '').trim();
  if (!normalizedDiseaseId) return char;
  return {
    ...char,
    diseaseList: toArray(char.diseaseList).map((entry) => (
      entry?.diseaseId === normalizedDiseaseId
        ? { ...entry, ...updates }
        : entry
    )),
  };
};

export const clearFoodEffectState = (
  char = {},
  settings = {},
  {
    removeWellFed = false,
    removeHungry = true,
    removeStarvation = true,
    resetStarving = false,
    resetDaysToStarvation = false,
  } = {},
) => {
  let nextChar = { ...char };
  const removalKeys = [];

  if (removeWellFed) {
    toArray(settings.wellFedBuffs).forEach((buff) => {
      removalKeys.push(buff);
      nextChar = removeLegacyEffect(nextChar, { text: buff, kind: 'buff', permanent: false });
    });
  }

  if (removeHungry) {
    toArray(settings.hungryDebuffs).forEach((debuff) => {
      removalKeys.push(debuff);
      nextChar = removeLegacyEffect(nextChar, { text: debuff, kind: 'debuff', permanent: false });
    });
  }

  if (removeStarvation) {
    toArray(settings.starvationDebuffs).forEach((debuff) => {
      removalKeys.push(debuff);
      nextChar = removeLegacyEffect(nextChar, { text: debuff, kind: 'debuff', permanent: false });
    });
  }

  if (removalKeys.length > 0) {
    const nextSources = { ...(nextChar.buffSources || {}) };
    removalKeys.forEach((key) => delete nextSources[key]);
    nextChar.buffSources = nextSources;
  }

  if (resetStarving) {
    nextChar.starving = false;
  }
  if (resetDaysToStarvation) {
    nextChar.daysToStarvation = settings.starvationThreshold || 7;
  }

  return nextChar;
};

export const applyWellFedState = (char = {}, settings = {}) => {
  let nextChar = clearFoodEffectState(char, settings, {
    removeWellFed: false,
    removeHungry: true,
    removeStarvation: true,
    resetStarving: true,
  });

  toArray(settings.wellFedBuffs).forEach((buff) => {
    nextChar = addLegacyEffect(nextChar, {
      text: buff,
      kind: 'buff',
      permanent: false,
      allowDuplicates: false,
      buffSource: 'Well Fed',
    });
  });

  return nextChar;
};

export const applyDietCompletionRewardState = (char = {}, diet = null, completionCount = 1) => {
  if (!diet || typeof diet !== 'object') return char;

  if (diet.name === 'Mixed') {
    return {
      ...char,
      weeklyStarvationBonus: Math.min(2, (char.weeklyStarvationBonus || 0) + 1),
    };
  }

  if (diet.name === 'Blended') {
    return {
      ...char,
      skillCheckBonus: Math.min(3, (char.skillCheckBonus || 0) + 1),
    };
  }

  let nextChar = { ...char };
  const source = `Diet: ${diet.name} (#${completionCount})`;
  toArray(diet.completionBuffs).forEach((buff) => {
    const allowDuplicates = buff.includes('Max HP');
    if (!allowDuplicates && toArray(nextChar.permanentBuffs).includes(buff)) return;
    nextChar = addLegacyEffect(nextChar, {
      text: buff,
      kind: 'buff',
      permanent: true,
      allowDuplicates,
      metadata: buildLegacyEffectMetadata({
        source,
        playerTooltip: `From completing the ${diet.name} diet`,
        colorId: 'teal',
        addedBy: 'Diet',
        noteVisible: true,
        provenance: {
          sourceType: 'diet',
          dietName: diet.name,
          completionCount,
        },
      }),
      buffSource: source,
    });
  });

  return nextChar;
};

export const removeSystemSourceStateInstance = (systemList = [], instanceKey, systems = {}) => {
  const list = Array.isArray(systemList) ? systemList : [];
  const target = list.find((instance) => (instance?.id || instance?.systemId) === instanceKey);
  if (!target) return list;

  const template = systems?.[target.systemId] || target.template;
  const cascade = !!template?.cascadeOnRemove;
  const targetKey = target.id || target.systemId;
  let nextList = list.filter((instance) => (instance?.id || instance?.systemId) !== instanceKey);

  if (cascade) {
    const childKeys = nextList
      .filter((instance) => instance?.parentSystemId === targetKey)
      .map((instance) => instance.id || instance.systemId);
    childKeys.forEach((childKey) => {
      nextList = removeSystemSourceStateInstance(nextList, childKey, systems);
    });
  }

  return nextList;
};

export const processSystemSourceStateExpiries = (systemList = [], tickKind, systems = {}) => {
  const list = Array.isArray(systemList) ? systemList : [];
  if (list.length === 0) {
    return { nextList: list, removedIds: [], notifications: [] };
  }

  const decremented = list.map((instance) => {
    const expiresIn = instance?.expiresIn;
    if (!expiresIn || typeof expiresIn !== 'object') return instance;
    const current = expiresIn[tickKind];
    if (typeof current !== 'number') return instance;
    return {
      ...instance,
      expiresIn: {
        ...expiresIn,
        [tickKind]: current - 1,
      },
    };
  });

  const expiredIds = decremented
    .filter((instance) => typeof instance?.expiresIn?.[tickKind] === 'number' && instance.expiresIn[tickKind] <= 0)
    .map((instance) => instance.id || instance.systemId);

  if (expiredIds.length === 0) {
    return { nextList: decremented, removedIds: [], notifications: [] };
  }

  const notifications = [];
  let nextList = decremented;
  expiredIds.forEach((instanceKey) => {
    const instance = nextList.find((entry) => (entry?.id || entry?.systemId) === instanceKey);
    if (instance) {
      const template = systems?.[instance.systemId] || instance.template;
      notifications.push(`⏳ ${template?.name || instance.systemId || 'System'} expired`);
    }
    nextList = removeSystemSourceStateInstance(nextList, instanceKey, systems);
  });

  return { nextList, removedIds: expiredIds, notifications };
};

const getItemEffectList = (char = {}, kind = 'buff') => (
  kind === 'debuff' ? toArray(char.debuffList) : toArray(char.buffList)
);

const getPriorArraysForItemEffectKind = (char = {}, kind = 'buff') => (
  kind === 'debuff'
    ? [toArray(char.permanentBuffs), toArray(char.buffList), toArray(char.permanentDebuffs)]
    : [toArray(char.permanentBuffs)]
);

const legacyEffectDataMatchesItem = (effectData = {}, item = {}) => {
  const provenance = effectData.provenance && typeof effectData.provenance === 'object'
    ? effectData.provenance
    : {};
  const itemIds = new Set([
    item.id,
    item.itemId,
    item.definitionId,
  ].filter(Boolean).map(String));
  if (itemIds.size === 0) return false;

  return [
    effectData.sourceItemEntryId,
    effectData.sourceItemId,
    effectData.sourceItemDefinitionId,
    provenance.itemEntryId,
    provenance.itemId,
    provenance.itemDefinitionId,
    provenance.definitionId,
  ].filter(Boolean).map(String).some((id) => itemIds.has(id));
};

const refreshLegacyItemSourceAttribution = (char = {}, text) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return char;

  const tempBuffIndex = toArray(char.buffList).findIndex((entry) => entry === normalizedText);
  if (tempBuffIndex >= 0) {
    const occurrence = getLegacyEffectGlobalOccurrence(
      normalizedText,
      tempBuffIndex,
      toArray(char.buffList),
      ...getPriorArraysForItemEffectKind(char, 'buff'),
    );
    const metadata = lookupLegacyEffectData(normalizedText, occurrence, char.buffData);
    if (metadata?.source) {
      return {
        ...char,
        buffSources: {
          ...(char.buffSources || {}),
          [normalizedText]: metadata.source,
        },
      };
    }
  }

  const tempDebuffIndex = toArray(char.debuffList).findIndex((entry) => entry === normalizedText);
  if (tempDebuffIndex >= 0) {
    const occurrence = getLegacyEffectGlobalOccurrence(
      normalizedText,
      tempDebuffIndex,
      toArray(char.debuffList),
      ...getPriorArraysForItemEffectKind(char, 'debuff'),
    );
    const metadata = lookupLegacyEffectData(normalizedText, occurrence, char.buffData);
    if (metadata?.source) {
      return {
        ...char,
        buffSources: {
          ...(char.buffSources || {}),
          [normalizedText]: metadata.source,
        },
      };
    }
  }

  if (char.buffSources?.[normalizedText] === undefined) return char;
  const nextSources = { ...(char.buffSources || {}) };
  delete nextSources[normalizedText];
  return {
    ...char,
    buffSources: nextSources,
  };
};

const findItemEffectIndex = (char = {}, text, kind = 'buff', item = {}) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return -1;
  const list = getItemEffectList(char, kind);
  const priorArrays = getPriorArraysForItemEffectKind(char, kind);

  for (let index = 0; index < list.length; index += 1) {
    if (list[index] !== normalizedText) continue;
    const occurrence = getLegacyEffectGlobalOccurrence(
      normalizedText,
      index,
      list,
      ...priorArrays,
    );
    const metadata = lookupLegacyEffectData(normalizedText, occurrence, char.buffData);
    if (legacyEffectDataMatchesItem(metadata || {}, item)) return index;
  }

  return list.lastIndexOf(normalizedText);
};

export const applyEquippedItemEffectState = (
  char = {},
  item = {},
  {
    removedItemBuffs = [],
    shouldRestoreOnEquip = true,
    addedBy = 'item-equip',
  } = {},
) => {
  if (!item || item.buffsApplyOnUse) return char;

  const blockedEffects = shouldRestoreOnEquip ? [] : toArray(removedItemBuffs);
  let nextChar = { ...char };
  const timestamp = new Date().toISOString();

  toArray(item.buffs).forEach((buff) => {
    if (blockedEffects.includes(buff)) return;
    const shouldWriteLegacySource = nextChar.buffSources?.[buff] === undefined;
    nextChar = addLegacyEffect(nextChar, {
      text: buff,
      kind: 'buff',
      permanent: false,
      allowDuplicates: true,
      metadata: buildLegacyEffectMetadata({
        source: item.name,
        addedAt: timestamp,
        addedBy,
        noteVisible: true,
        provenance: {
          sourceType: 'item',
          itemId: item.id,
          itemName: item.name,
          mode: 'equip',
        },
        extra: {
          sourceItemId: item.id,
        },
      }),
      buffSource: shouldWriteLegacySource ? item.name : null,
    });
  });

  toArray(item.debuffs).forEach((debuff) => {
    if (blockedEffects.includes(debuff)) return;
    const shouldWriteLegacySource = nextChar.buffSources?.[debuff] === undefined;
    nextChar = addLegacyEffect(nextChar, {
      text: debuff,
      kind: 'debuff',
      permanent: false,
      allowDuplicates: true,
      metadata: buildLegacyEffectMetadata({
        source: item.name,
        addedAt: timestamp,
        addedBy,
        noteVisible: true,
        provenance: {
          sourceType: 'item',
          itemId: item.id,
          itemName: item.name,
          mode: 'equip',
        },
        extra: {
          sourceItemId: item.id,
        },
      }),
      buffSource: shouldWriteLegacySource ? item.name : null,
    });
  });

  return nextChar;
};

export const removeEquippedItemEffectState = (char = {}, item = {}) => {
  if (!item || item.buffsApplyOnUse) return char;
  let nextChar = { ...char };

  toArray(item.buffs).forEach((buff) => {
    const index = findItemEffectIndex(nextChar, buff, 'buff', item);
    if (index < 0) return;
    nextChar = removeLegacyEffectAt(nextChar, {
      kind: 'buff',
      permanent: false,
      index,
      text: buff,
    });
    nextChar = refreshLegacyItemSourceAttribution(nextChar, buff);
  });

  toArray(item.debuffs).forEach((debuff) => {
    const index = findItemEffectIndex(nextChar, debuff, 'debuff', item);
    if (index < 0) return;
    nextChar = removeLegacyEffectAt(nextChar, {
      kind: 'debuff',
      permanent: false,
      index,
      text: debuff,
    });
    nextChar = refreshLegacyItemSourceAttribution(nextChar, debuff);
  });

  return nextChar;
};

export const getEquippedItemEffectPatch = (char = {}, item = {}, options = {}) => {
  const nextChar = options?.remove
    ? removeEquippedItemEffectState(char, item)
    : applyEquippedItemEffectState(char, item, options);

  return getLegacyEffectPatch(char, nextChar, [
    'buffList',
    'debuffList',
    'buffSources',
    'buffData',
    'buffNotes',
  ]);
};
